import { useMemo, useState } from "react";
import { Link, useLoaderData, useRevalidator, useSubmit } from "react-router-dom";
import { saveAs } from "file-saver";

import FileIcon from "src/components/common/fileIcons";
import Footer from "src/components/common/frame/footer";
import { getFileNameFromUrl, myAxios, showSize } from "src/components/common/functions";
import { useUploadStore } from "src/components/main/uploadStore";
import { useAuthStore, User } from "src/components/main/authStore";

import type { DocumentItem, DocumentLoaderData } from "./actions_loaders";

export default function DocumentsPage({
    modulePath,
}: {
    modulePath: "/Documents" | "/Safety";
}) {
    const { rootFolderId, currentFolderId, files, parents } = useLoaderData() as DocumentLoaderData;
    const user = useAuthStore(state => state.user) as User;
    const upload = useUploadStore();
    const submit = useSubmit();
    const revalidator = useRevalidator();
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [filterWords, setFilterWords] = useState("");
    const canEdit = user.projects !== "no";
    const filteredFiles = useMemo(
        () => files.filter(item => JSON.stringify(item).toLowerCase().includes(filterWords.toLowerCase())),
        [files, filterWords]
    );
    const currentItems = filteredFiles.slice((currentPage - 1) * Number(itemsPerPage), currentPage * Number(itemsPerPage));

    const mutate = (kind: "createFolder" | "createLink" | "rename" | "delete", values: Record<string, string>) => {
        const formData = new FormData();
        formData.append("rootFolderId", rootFolderId);
        Object.entries(values).forEach(([key, value]) => formData.append(key, value));
        submit(formData, {
            method: "post",
            action: `_action/${kind}`,
        });
    };

    const handleUpload = async (target: HTMLInputElement) => {
        const selectedFiles = Array.from(target.files ?? []);
        if (!selectedFiles.length) return;
        const formData = new FormData();
        formData.append("folderId", currentFolderId);
        formData.append("rootFolderId", rootFolderId);
        selectedFiles.forEach(file => {
            formData.append("fileName[]", file.name);
            formData.append("fileSize[]", String(file.size));
            formData.append("fileType[]", file.type || "application/octet-stream");
            formData.append("lastModifiedAt[]", new Date(file.lastModified).toISOString().slice(0, 19).replace("T", " "));
        });
        const res = await myAxios({
            fileName: "documents/uploadFiles.php",
            formData,
        });
        if (!res) return;
        upload.setFiles(selectedFiles);
        upload.setUrls(res.data.urls);
        upload.setIds(res.data.ids);
        upload.setShow(true);
        upload.setOnResetCallback(() => {
            revalidator.revalidate();
        });
        target.value = "";
    };

    const handleDownload = async (doc: DocumentItem) => {
        const formData = new FormData();
        formData.append("fileId", doc.id);
        formData.append("fileName", doc.name);
        formData.append("rootFolderId", rootFolderId);
        const res = await myAxios({ fileName: "documents/downloadFile.php", formData });
        if (!res) return;
        const url = String(res.data ?? "");
        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error("Failed to fetch file");
            saveAs(await response.blob(), getFileNameFromUrl(url) || doc.name);
        } catch (error) {
            window.alert("Error downloading file: " + error);
        }
    };

    return (
        <div className = "cssContent pb8rem">
            <nav className = "my-3 d-flex align-items-center flex-wrap gap-3">
                <div className = "fw-bold text-success">
                    {parents.map((parent, index) => (
                        <span key = {parent.id}>
                            {index ? <span className = "mx-2">&rarr;</span> : null}
                            {parent.id === currentFolderId ? (
                                <span>{parent.name}</span>
                            ) : (
                                <Link to = {parent.id === rootFolderId ? modulePath : `${modulePath}/${parent.id}`}>{parent.name}</Link>
                            )}
                        </span>
                    ))}
                </div>
                <label className = "mb-0" htmlFor = "documents-search">Search</label>
                <input
                    id = "documents-search"
                    className = "form-control inline w-auto"
                    value = {filterWords}
                    onChange = {e => {
                        setCurrentPage(1);
                        setFilterWords(e.currentTarget.value);
                    }}
                />
                {canEdit ? (
                    <>
                        <label className = "btn btn-outline-primary mb-0" htmlFor = "documents-upload">
                            Upload Files
                            <input
                                id = "documents-upload"
                                type = "file"
                                multiple
                                className = "nodisplay"
                                onChange = {e => void handleUpload(e.currentTarget)}
                            />
                        </label>
                        <button
                            type = "button"
                            className = "btn btn-outline-primary"
                            onClick = {() => {
                                let folderName = window.prompt("Folder Name:");
                                if (folderName === null) return;
                                while (folderName === "") {
                                    window.alert("Folder Name can not be empty.");
                                    folderName = window.prompt("Folder Name:");
                                    if (folderName === null) return;
                                }
                                mutate("createFolder", { parentFolderId: currentFolderId, folderName });
                            }}
                        >
                            Add Folder
                        </button>
                        <button
                            type = "button"
                            className = "btn btn-outline-primary"
                            onClick = {() => {
                                let linkName = window.prompt("The name of the link:");
                                if (linkName === null) return;
                                while (linkName === "") {
                                    window.alert("The name can not be empty.");
                                    linkName = window.prompt("The name of the link:");
                                    if (linkName === null) return;
                                }
                                let linkAddress = window.prompt("The address of the link:");
                                if (linkAddress === null) return;
                                while (linkAddress === "") {
                                    window.alert("The address can not be empty.");
                                    linkAddress = window.prompt("The address of the link:");
                                    if (linkAddress === null) return;
                                }
                                mutate("createLink", { parentFolderId: currentFolderId, linkName, linkAddress });
                            }}
                        >
                            Add Link
                        </button>
                    </>
                ) : null}
            </nav>
            {!filteredFiles.length ? (
                <div className = "cssLoading">Empty</div>
            ) : (
                <div className = "table-responsive">
                    <table className = "table">
                        <thead className = "noTableTopBorder">
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Size</th>
                                <th />
                                {canEdit ? <th /> : null}
                                {canEdit ? <th /> : null}
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map(doc => {
                                const isFolder = doc.type === "folder";
                                const isLink = doc.type === "link";
                                const href = isFolder ? `${modulePath}/${doc.id}` : isLink ? doc.description : "";
                                return (
                                    <tr key = {doc.id}>
                                        <td>
                                            {isFolder ? (
                                                <Link to = {href} className = "fw-bold text-primary">
                                                    <FileIcon fileType = "" isFolder = {true} className = "me-2" />
                                                    {doc.name}
                                                </Link>
                                            ) : isLink ? (
                                                <a href = {doc.description} target = "_blank" rel = "noreferrer">
                                                    <FileIcon fileType = "link" isFolder = {false} className = "me-2" />
                                                    {doc.name}
                                                </a>
                                            ) : (
                                                <span>
                                                    <FileIcon fileType = {doc.name.split(".").at(-1) ?? ""} isFolder = {false} className = "me-2" />
                                                    {doc.name}
                                                </span>
                                            )}
                                        </td>
                                        <td>{isFolder ? "Folder" : isLink ? "Link" : doc.type}</td>
                                        <td>{isFolder || isLink ? "" : showSize(doc.size)}</td>
                                        <td>
                                            {!isFolder && !isLink ? (
                                                <button type = "button" className = "btn btn-outline-primary" onClick = {() => void handleDownload(doc)}>
                                                    Download
                                                </button>
                                            ) : null}
                                        </td>
                                        {canEdit ? (
                                            <td>
                                                <button
                                                    type = "button"
                                                    className = "btn btn-outline-secondary"
                                                    onClick = {() => {
                                                        let fileName = window.prompt("Name:", doc.name);
                                                        if (fileName === null) return;
                                                        while (fileName === "") {
                                                            window.alert("The name cannot be empty.");
                                                            fileName = window.prompt("Name:", doc.name);
                                                            if (fileName === null) return;
                                                        }
                                                        mutate("rename", { fileId: doc.id, fileName });
                                                    }}
                                                >
                                                    Rename
                                                </button>
                                            </td>
                                        ) : null}
                                        {canEdit ? (
                                            <td>
                                                <button
                                                    type = "button"
                                                    className = "btn btn-danger"
                                                    onClick = {() => {
                                                        if (!window.confirm(`Are you sure to delete this ${isFolder ? "folder" : "document"}?`)) return;
                                                        mutate("delete", { fileId: doc.id });
                                                    }}
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        ) : null}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            {filteredFiles.length ? (
                <Footer
                    length = {filteredFiles.length}
                    currentPage = {currentPage}
                    setCurrentPage = {setCurrentPage}
                    itemsPerPage = {itemsPerPage}
                    setItemsPerPage = {setItemsPerPage}
                />
            ) : null}
        </div>
    );
}
